import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { ParsedUrlQuery } from "querystring";
import React, { useEffect, useState } from "react";

import { DEFAULT_SEARCH_PER_PAGE_VALUE } from "@/shared/constants";

import { UI_CONTEXTS } from "@/analytics/constants";

import { useCurrentLocale } from "@/context/LanguageContext";

import ActionButton from "@/components/buttons/action-button";
import { KINDS, SIZES } from "@/components/buttons/buttons.constants";
import { DropdownMenuItem } from "@/components/dropdown-menus/dropdown-menu-items/dropdown-menu-item-interface";
import styles from "@/components/store-directory/storeDirectory.module.scss";

import { useSearchStores } from "@/hooks/useSearch";

import { StoreProps, StoresWithCountProps } from "@/interfaces/stores-interface";

import { ControlHeader } from "../control-header/control-header-search";
import { StoreTiles } from "../store-tiles/store-tiles";

interface Props {
    searchVersion: string;
    searchTerm: string;
    httpCorrelationId: string;
    storesSearchResults?: StoresWithCountProps;
}

const StoreDirectorySearch: React.FC<Props> = ({
    searchVersion,
    searchTerm,
    storesSearchResults,
    httpCorrelationId,
}) => {
    const locale = useCurrentLocale();
    const router = useRouter();
    const { t } = useTranslation();

    const sortByOptions: Array<DropdownMenuItem> = [
        {
            id: 1,
            name: t("store.filterRelevanceOptionText"),
            value: "relevance",
        },
        {
            id: 2,
            name: t("store.filterNewestOptionText"),
            value: "newest",
        },
        {
            id: 3,
            name: t("store.filterMostPopularOptionText"),
            value: "most_popular",
        },
    ];
    const defaultSort = sortByOptions[0].value;
    const [sortByValue, setSortByValue] = useState(router.query.sort_by ?? defaultSort);
    const [hasOffers, setHasOffers] = useState(router.query.has_offer === "true");
    const [queryString, setQueryString] = useState<ParsedUrlQuery>({
        per_page: String(DEFAULT_SEARCH_PER_PAGE_VALUE),
        has_in_store: String(false),
        has_offer: String(hasOffers),
        sort_by: sortByValue,
        search_version: searchVersion,
        q: searchTerm,
        cor_id: httpCorrelationId,
        locale: locale,
    });

    const { data: results } = useSearchStores<StoreProps>("/api/stores/search", {
        initialState: storesSearchResults,
        query: queryString,
    });

    useEffect(() => {
        setHasOffers(router.query.has_offer === "true");
        setSortByValue(router.query.sort_by ?? defaultSort);
    }, [router.query.has_offer, router.query.sort_by, defaultSort]);

    useEffect(() => {
        const { sort_by, has_offer } = router.query;
        if (sort_by && typeof sort_by === "string") {
            setSortByValue(sort_by);
        }
        setHasOffers(has_offer === "true");
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setQueryString({
            per_page: String(DEFAULT_SEARCH_PER_PAGE_VALUE),
            has_in_store: String(false),
            has_offer: String(hasOffers),
            sort_by: sortByValue,
            search_version: searchVersion,
            q: searchTerm,
            cor_id: httpCorrelationId,
            locale: locale,
        });
    }, [hasOffers, sortByValue, locale, searchVersion, searchTerm, httpCorrelationId]);

    const pushState = (params) => {
        const queryParams = { ...router.query, ...params };
        if (!queryParams.has_offer) delete queryParams.has_offer;
        if (queryParams.sort_by === defaultSort) delete queryParams.sort_by;
        router.push({ pathname: router.pathname, query: queryParams }, undefined, {
            shallow: true,
        });
    };

    const clearFilters = async () => {
        pushState({ has_offer: false });
    };

    return (
        <div data-testid="store-directory-search">
            <ControlHeader
                locale={locale}
                hasOffers={hasOffers}
                handleOffersClick={() => pushState({ has_offer: !hasOffers })}
                handleSortByClick={(val) => pushState({ sort_by: val })}
                sortByOptions={sortByOptions}
                searchVersion={searchVersion}
                searchTerm={searchTerm}
                storeCount={results.totalCount}
                uiContext={UI_CONTEXTS.SEARCH}
            />
            {!results.data.length ? (
                <div className={styles.emptyResultsContainer}>
                    <div className={styles.emptyResultsText}>
                        <p>{t("store.filters.notFound.noResults")}</p>
                    </div>
                    <ActionButton
                        disabled={false}
                        fluid={false}
                        kind={KINDS.PRIMARY}
                        label={t("store.filters.notFound.filterButtonText")}
                        size={SIZES.LARGE}
                        onClick={clearFilters}
                    />
                </div>
            ) : (
                <StoreTiles
                    stores={results.data}
                    analytics={{
                        uiContext: UI_CONTEXTS.SEARCH_TILE,
                        searchTerm,
                        eventProps: {
                            httpCorrelationId,
                        },
                    }}
                />
            )}
        </div>
    );
};

export default StoreDirectorySearch;
